import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/context/auth-context';
import { httpClient as http } from '@/lib/api';

export type Visibility = 'NONE' | 'OWN' | 'TEAM' | 'ALL';
export type AccessAction = 'read' | 'create' | 'update' | 'delete' | 'validate';
export interface Grant {
  resource: string;
  visibility: Visibility;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canValidate: boolean;
}
export interface AccessRole {
  id: string;
  name: string;
  description: string;
  systemKey: string | null;
  updatedAt: string;
  permissions: Grant[];
  _count: { users: number };
}
export interface Team {
  id: string;
  name: string;
  description: string;
  updatedAt: string;
  members: { userId: string }[];
}
export interface Staff {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Avatar du collaborateur, ou `null` s’il n’en a pas encore. */
  avatarUrl: string | null;
  status: string;
  accessRoleId: string | null;
  updatedAt: string;
  teamMemberships: { teamId: string }[];
}
export interface AccessSnapshot {
  role: Pick<AccessRole, 'id' | 'name' | 'systemKey'> | null;
  isAdmin: boolean;
  permissions: Grant[];
  teams: { id: string; name: string }[];
}
export interface Overview {
  roles: AccessRole[];
  teams: Team[];
  users: Staff[];
  resources: { key: string; label: string }[];
  audit: { id: string; actorId: string; targetId: string; action: string; createdAt: string }[];
}

export const can = (
  access: AccessSnapshot | undefined,
  resource: string,
  action: AccessAction = 'read',
) => {
  if (!access) return false;
  if (access.isAdmin) return true;
  const grant = access.permissions.find((entry) => entry.resource === resource);
  if (!grant || grant.visibility === 'NONE') return false;
  if (action === 'read') return true;
  return grant[
    { create: 'canCreate', update: 'canUpdate', delete: 'canDelete', validate: 'canValidate' }[
      action
    ] as 'canCreate'
  ];
};
export const pathPermission = (path: string): { resource: string; action: AccessAction } | null => {
  const normalized = path.split('?')[0]?.replace(/\/$/, '');
  if (normalized?.startsWith('/imports/besoins/'))
    return { resource: 'requirements', action: 'read' };
  if (normalized === '/parametres' || normalized === '/parametres/preferences') return null;
  if (normalized === '/parametres/corbeille-candidats')
    return { resource: 'candidate-trash', action: 'read' };
  const module = path.split(/[/?]/)[1];
  if (module === 'parametres')
    return { resource: path.includes('acces') ? 'access' : 'reference', action: 'read' };
  const resource = (
    {
      dashboard: 'dashboard',
      candidat: 'candidates',
      candidats: 'candidates',
      societes: 'companies',
      contacts: 'contacts',
      actions: 'actions',
      entretiens: 'actions',
      besoins: 'requirements',
      projets: 'projects',
      adv: 'adv',
      achats: 'adv',
      prospection: 'prospection',
      cra: 'timesheets',
      messagerie: 'messaging',
    } as Record<string, string>
  )[module ?? ''];
  return resource
    ? { resource, action: /nouveau|nouvelle|importer|import=/.test(path) ? 'create' : 'read' }
    : null;
};
export const canVisit = (access: AccessSnapshot | undefined, path: string) => {
  if (['/imports', '/candidats/imports'].includes(path.split('?')[0] ?? ''))
    return can(access, 'candidates') || can(access, 'requirements');
  if (path.split('?')[0] === '/societes')
    return can(access, 'companies') || can(access, 'contacts');
  const permission = pathPermission(path);
  return (
    !permission ||
    (permission.resource === 'access'
      ? canManageAccess(access)
      : can(access, permission.resource, permission.action))
  );
};
export const useAccess = () => {
  const { user, status } = useAuth();
  const query = useQuery({
    queryKey: ['access-me', user?.id],
    queryFn: () => http.get<AccessSnapshot>('/access/me'),
    enabled: status === 'authenticated',
    staleTime: 10_000,
    refetchInterval: 30_000,
    retry: 1,
  });
  return {
    ...query,
    can: (resource: string, action: AccessAction = 'read') => can(query.data, resource, action),
  };
};
export const visibilityOptions = [
  { value: 'NONE', label: 'Aucune' },
  { value: 'OWN', label: 'Mes données' },
  { value: 'TEAM', label: 'Équipe' },
  { value: 'ALL', label: 'Toutes' },
] as const;

export const administrationResources = ['users', 'roles', 'permissions', 'teams'] as const;
export const canManageAccess = (access: AccessSnapshot | undefined) =>
  administrationResources.some((resource) => can(access, resource));
export const canDelegateRole = (access: AccessSnapshot | undefined, role: AccessRole) => {
  if (access?.isAdmin) return true;
  if (!access || role.systemKey === 'ADMIN') return false;
  const rank = { NONE: 0, OWN: 1, TEAM: 2, ALL: 3 };
  return role.permissions.every((grant) => {
    const own = access.permissions.find((item) => item.resource === grant.resource);
    return (
      rank[grant.visibility] <= rank[own?.visibility ?? 'NONE'] &&
      (['canCreate', 'canUpdate', 'canDelete', 'canValidate'] as const).every(
        (key) => !grant[key] || !!own?.[key],
      )
    );
  });
};
